import { useEffect, useState } from 'react';
import { Flex, Input, Modal, Table, Tag, Typography } from 'antd';
import { useGlobalContext } from '@contexts/global';
import { TransferOriginLocationSearchHook } from '@hooks/location';
import type { InventorySuggestedTransferItem } from 'schemas/inventorySuggestedTransfers';
import type { TransferSourceInventoryRow } from 'schemas/transferSourceInventory';
import { InventoryService, ToastMessage } from '@utility/index';

const { Text } = Typography;

type OriginSuggestionModalProps = {
  open: boolean;
  item?: InventorySuggestedTransferItem | null;
  onClose: () => void;
  refresh: () => void;
};

export function OriginSuggestionModal({
  open,
  item,
  onClose,
  refresh,
}: OriginSuggestionModalProps) {
  const { warehouseId } = useGlobalContext();
  const [selectedInventoryId, setSelectedInventoryId] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { data, loading, filterChanged, filter, totalData, pageChanged } =
    TransferOriginLocationSearchHook(
      warehouseId,
      item?.productId?._id ?? '',
      item?._id ?? '',
    );

  useEffect(() => {
    if (open) {
      setSelectedInventoryId(item?.inventoryId?._id ?? null);
    }
  }, [item?.inventoryId?._id, open]);

  const handleClose = () => {
    if (confirmLoading) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedInventoryId || !item?._id) return;

    if (selectedInventoryId === item.inventoryId?._id) {
      onClose();
      return;
    }

    setConfirmLoading(true);
    try {
      await InventoryService.updateSuggestedTransfer(item._id, {
        inventoryId: selectedInventoryId,
      });
      ToastMessage.success('Origin updated successfully');
      refresh();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns = [
    {
      title: 'Location',
      key: 'location',
      render: (_: unknown, row: TransferSourceInventoryRow) => (
        <div>
          {row.sourceLocation?.zoneId ? (
            <Tag color={row.sourceLocation.zoneId.color}>
              {row.sourceLocation.zoneId.code}
            </Tag>
          ) : null}
          <br />
          {row.sourceLocation?.code ? (
            <Tag className="mt-1" color={row.sourceLocation.zoneId?.color}>
              {row.sourceLocation.code}
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Lot',
      key: 'lotNumber',
      dataIndex: 'lotNumber',
      render: (lotNumber: string | null) => lotNumber || '—',
    },
    {
      title: 'Alias',
      key: 'cartonAlias',
      dataIndex: 'cartonAlias',
      render: (cartonAlias: string | null) => cartonAlias || '—',
    },
    {
      title: 'Pickable',
      key: 'pickableQuantity',
      render: (_: unknown, row: TransferSourceInventoryRow) => (
        <Flex align="center" gap={4}>
          <Text>{row.pickableQuantity}</Text>
          <Text type="secondary">Units</Text>
        </Flex>
      ),
    },
    {
      title: 'Pallet',
      key: 'pallet',
      render: (_: unknown, row: TransferSourceInventoryRow) =>
        row.pallet?.palletNumber || '—',
    },
  ];

  const pagination = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50],
    showTotal: (total: number) => `${total} source inventory rows`,
    onChange: (page: number, pageSize: number) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Modal
      title="Edit Origin"
      open={open}
      centered
      width={900}
      onCancel={handleClose}
      onOk={handleConfirm}
      okText="Update Origin"
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !selectedInventoryId }}
      cancelButtonProps={{ disabled: confirmLoading }}
      destroyOnHidden
    >
      <div className="mb-3">
        <Text type="secondary">SKU</Text>
        <Text strong className="ml-2">
          {item?.productId?.p_sku || 'N/A'}
        </Text>
        <Text type="secondary" className="ml-3">
          Lot
        </Text>
        <Text strong className="ml-2">
          {item?.inventoryId?.lotNumber || 'N/A'}
        </Text>
        <Text type="secondary" className="ml-3">
          Suggested Quantity
        </Text>
        <Text strong className="ml-2">
          {item?.suggestedQuantity ?? 'N/A'}
        </Text>
      </div>

      <Input.Search
        allowClear
        placeholder="Search by location, lot, carton..."
        value={filter.search}
        onChange={event => filterChanged('search', event.target.value)}
        className="mb-3"
      />

      <Table
        rowKey="inventoryId"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        pagination={pagination}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: selectedInventoryId ? [selectedInventoryId] : [],
          onChange: selectedKeys => {
            setSelectedInventoryId(
              selectedKeys.length > 0 ? String(selectedKeys[0]) : null,
            );
          },
        }}
        onRow={row => ({
          onClick: () => setSelectedInventoryId(row.inventoryId),
          style: { cursor: 'pointer' },
        })}
      />
    </Modal>
  );
}
